import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Plus, Loader2, Users } from 'lucide-react';
import { contactosAPI } from '../api';

/**
 * Selector de co-titulares informativos de una factura.
 *
 * NO son destinatarios fiscales: solo aparecen en el PDF como
 * "También obligados al pago: X, Y" y para control interno.
 *
 * Props:
 *  - value: array de objetos { id, nombre, nif_cif, ... } | array de IDs
 *  - onChange(arrayOfObjects): se invoca con la nueva lista de cotitulares
 *  - excludeIds: ids de contactos a no permitir (ej. el cliente principal)
 */
const CotitularesSelector = ({ value = [], onChange, excludeIds = [] }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState([]);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Si llegan IDs sin datos, hidrátalos
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = [];
      for (const v of value || []) {
        if (typeof v === 'object' && v && v.id) {
          out.push(v);
        } else if (v) {
          try {
            const res = await contactosAPI.getById(v);
            const c = res.data?.data || res.data;
            if (c && c.id) out.push(c);
          } catch (e) { /* ignore */ }
        }
      }
      if (!cancelled) setHydrated(out);
    })();
    return () => { cancelled = true; };
  }, [JSON.stringify(value)]); // eslint-disable-line

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleQuery = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await contactosAPI.getAll({ search: q, limit: 20 });
        const data = res.data?.data || res.data || [];
        setResults(Array.isArray(data) ? data : []);
      } catch (e) { setResults([]); }
      setSearching(false);
    }, 300);
  };

  const addCotitular = (c) => {
    if (!c || !c.id) return;
    const idStr = String(c.id);
    if (excludeIds.map(String).includes(idStr)) return;
    if (hydrated.some(h => String(h.id) === idStr)) return;
    const next = [...hydrated, c];
    setHydrated(next);
    onChange(next);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const removeCotitular = (id) => {
    const next = hydrated.filter(c => String(c.id) !== String(id));
    setHydrated(next);
    onChange(next);
  };

  const filteredResults = results.filter(r =>
    !hydrated.some(h => String(h.id) === String(r.id)) &&
    !excludeIds.map(String).includes(String(r.id))
  );

  return (
    <div ref={containerRef} className="space-y-2" data-testid="cotitulares-selector">
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <Users className="h-4 w-4" />
        <span className="font-medium">Co-titulares (informativo)</span>
        <span className="text-xs text-gray-500">— también obligados al pago, no destinatarios fiscales</span>
      </div>

      {/* Lista de co-titulares como chips */}
      {hydrated.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="cotitulares-chips">
          {hydrated.map(c => (
            <div
              key={c.id}
              className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-900 text-xs"
              data-testid={`cotitular-chip-${c.id}`}
            >
              <span className="font-medium">{c.nombre_completo || c.nombre}</span>
              {c.nif_cif && <span className="font-mono text-blue-700">({c.nif_cif})</span>}
              <button
                type="button"
                onClick={() => removeCotitular(c.id)}
                className="text-blue-600 hover:text-blue-900 ml-1"
                data-testid={`cotitular-remove-${c.id}`}
                aria-label="Quitar co-titular"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Buscador */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => { handleQuery(e.target.value); setOpen(true); }}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder="Añadir co-titular: buscar por nombre o NIF..."
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid="cotitular-search-input"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
          )}
        </div>

        {open && (query.length >= 2) && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-56 overflow-auto">
            {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
            {!searching && filteredResults.length === 0 && (
              <div className="p-3 text-sm text-center text-gray-500">No se encontraron contactos</div>
            )}
            {filteredResults.map(c => (
              <div
                key={c.id}
                onClick={() => addCotitular(c)}
                className="px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center gap-2 text-sm"
                data-testid={`cotitular-result-${c.id}`}
              >
                <Plus className="h-4 w-4 text-blue-600" />
                <span className="font-medium">{c.nombre_completo || c.nombre}</span>
                {c.nif_cif && <span className="text-xs text-gray-500 font-mono">({c.nif_cif})</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CotitularesSelector;
